export interface LanguageOption {
  code: string
  label: string
}

export const LANGUAGES_DASHBOARD_TYPES = {
  VI: 'vi-south',
  ENG: 'en',
} as const

export type LanguagesType =
  (typeof LANGUAGES_DASHBOARD_TYPES)[keyof typeof LANGUAGES_DASHBOARD_TYPES]

export const DASHBOARD_LANGUAGE_OPTIONS = [
  { label: 'VI', locale: LANGUAGES_DASHBOARD_TYPES.VI, value: LANGUAGES_DASHBOARD_TYPES.VI },
  { label: 'EN', locale: LANGUAGES_DASHBOARD_TYPES.ENG, value: LANGUAGES_DASHBOARD_TYPES.ENG },
] as const

export function getDashboardLanguageLocale(language: string | null | undefined): string {
  const normalizedLanguage = language?.trim().toLowerCase()
  return (
    DASHBOARD_LANGUAGE_OPTIONS.find(
      (option) => option.value === normalizedLanguage || option.locale === normalizedLanguage,
    )?.locale ?? LANGUAGES_DASHBOARD_TYPES.ENG
  )
}

export const AVAILABLE_LANGUAGES: LanguageOption[] = [
  { code: 'ar', label: 'العربية' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fi', label: 'Suomi' },
  { code: 'fr', label: 'Français' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'it', label: 'Italiano' },
  { code: 'ja', label: '日本語' },
  { code: 'km', label: 'ខ្មែរ' },
  { code: 'ko', label: '한국어' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'pl', label: 'Polski' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'th', label: 'ไทย' },
  { code: 'uz', label: 'O`zbek' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'vi-central', label: 'Tiếng Việt - Miền Trung' },
  { code: 'vi-human', label: 'Tiếng Việt - Human' },
  { code: 'vi-north', label: 'Tiếng Việt - Miền Bắc' },
  { code: 'vi-south', label: 'Tiếng Việt - Miền Nam' },
  { code: 'zh', label: '中文' },
  { code: 'zh-traditional', label: '中文 - Traditional' },
]

const LANGUAGE_VARIANT_LABELS: Record<string, string> = {
  central: 'Miền Trung',
  north: 'Miền Bắc',
  south: 'Miền Nam',
  traditional: 'Traditional',
  simplified: 'Simplified',
  human: 'Human',
}

function formatVariantToken(token: string): string {
  return (
    LANGUAGE_VARIANT_LABELS[token] ??
    token
      .split(/[_\s]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  )
}

/**
 * Native spelling of a language ("العربية", "Tiếng Việt - Miền Nam").
 *
 * Only used as the fallback for {@link resolveLanguageDisplayName} when the runtime has no
 * CLDR data for a code. For anything user-facing use the `useLanguageLabel` hook, which
 * feeds the current UI locale in as a reactive value.
 */
function resolveNativeLanguageLabel(code: string): string {
  const normalizedCode = code.trim().toLowerCase()
  if (!normalizedCode) {
    return code
  }

  const exactMatch = AVAILABLE_LANGUAGES.find(
    (language) => language.code.toLowerCase() === normalizedCode,
  )
  if (exactMatch) {
    return exactMatch.label
  }

  const [baseCode, ...variantParts] = normalizedCode.split('-')
  const baseLanguage = AVAILABLE_LANGUAGES.find(
    (language) => language.code.toLowerCase() === baseCode,
  )

  if (!baseLanguage) {
    return code
      .split(/[-_]/)
      .map((part) =>
        part.toUpperCase() === part ? part : part.charAt(0).toUpperCase() + part.slice(1),
      )
      .join(' ')
  }

  if (variantParts.length === 0) {
    return baseLanguage.label
  }

  const variantLabel = variantParts.map(formatVariantToken).join(' - ')
  return `${baseLanguage.label} - ${variantLabel}`
}

const VARIANT_LABELS_BY_LOCALE: Record<string, Record<string, string>> = {
  vi: {
    central: 'Miền Trung',
    north: 'Miền Bắc',
    south: 'Miền Nam',
    traditional: 'Phồn thể',
    simplified: 'Giản thể',
    human: 'Human',
  },
  en: {
    central: 'Central',
    north: 'Northern',
    south: 'Southern',
    traditional: 'Traditional',
    simplified: 'Simplified',
    human: 'Human',
  },
}

function formatVariantTokenByLocale(token: string, locale: string): string {
  const normalizedLocale = locale.toLowerCase()
  // * VI: Locale VI của dashboard là `vi-south`, phải tra theo subtag gốc `vi` nếu không sẽ rơi về từ điển `en`.
  // * EN: The dashboard's VI locale is `vi-south`, so fall back to the base subtag `vi` before the `en` dictionary.
  const baseLocale = normalizedLocale.split('-')[0] ?? normalizedLocale
  const dictionary =
    VARIANT_LABELS_BY_LOCALE[normalizedLocale] ??
    VARIANT_LABELS_BY_LOCALE[baseLocale] ??
    VARIANT_LABELS_BY_LOCALE['en']
  if (!dictionary) {
    return token
  }
  return (
    dictionary[token] ??
    token
      .split(/[_\s]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  )
}

function getLanguageDisplayName(baseCode: string, locale: string): string | undefined {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' })
    const value = displayNames.of(baseCode)
    if (!value) {
      return undefined
    }
    return value.charAt(0).toUpperCase() + value.slice(1)
  } catch {
    return undefined
  }
}

export function resolveLanguageDisplayName(code: string, locale: string): string {
  const normalizedCode = code.trim().toLowerCase()
  if (!normalizedCode) {
    return code
  }

  const [rawBaseCode, ...variantParts] = normalizedCode.split('-')
  const baseCode = rawBaseCode ?? normalizedCode
  const baseLanguageName =
    getLanguageDisplayName(baseCode, locale) ?? resolveNativeLanguageLabel(baseCode)

  if (variantParts.length === 0) {
    return baseLanguageName
  }

  const normalizedLocale = locale.toLowerCase()
  const variantLabel = variantParts
    .map((part) => formatVariantTokenByLocale(part, normalizedLocale))
    .join(' - ')

  return `${baseLanguageName} - ${variantLabel}`
}
